"use client";

import { useEffect, useRef, useState } from "react";
import * as THREE from "three";

export interface UseTypedTextureOptions {
  text: string; // Full text
  speed?: number; // Characters per second
  size?: number; // Canvas resolution
  font?: string; // 2D-canvas font string
  color?: string; // Text colour
  padding?: number; // Pixel padding
  progress: number;        // 👈 NUEVO: 0-1 controlado desde fuera
}

/**
 * Returns a THREE.CanvasTexture that reveals `text` progressively.
 * May return `null` on the very first render (during SSR).
 */
export const useTypedTexture = ({
  text,
  progress,              // ← entra cada re-render
  size = 1_024,
  font = '48px "Patrick Hand", sans-serif',
  color = "#000",
  padding = 64,
}: UseTypedTextureOptions): THREE.CanvasTexture | null => {
  // DOM-dependent objects live in refs
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const textureRef = useRef<THREE.CanvasTexture | null>(null);

  // Expose the texture to the caller; starts as null on the server
  const [, setTexture] = useState<THREE.CanvasTexture | null>(null);

  /**
   * This effect ONLY runs in the browser, never during SSR.
   * We create the canvas + texture once and store them.
   */
  useEffect(() => {
    // Guard in case someone force-mounts on the server
    if (typeof document === "undefined") return;

    // 1. Create or reuse canvas
    if (!canvasRef.current) {
      const c = document.createElement("canvas");
      c.width = size;
      c.height = size;
      canvasRef.current = c;
    }

    // 2. Create or reuse texture
    if (!textureRef.current) {
      textureRef.current = new THREE.CanvasTexture(canvasRef.current);
      setTexture(textureRef.current); // notify caller
    }
  }, [size]);

  /**
   * Draw / animate every time the content or styling changes.
   */
  /** Redibuja cada vez que cambie `progress` o el styling */
  useEffect(() => {
    if (!canvasRef.current || !textureRef.current) return;
    const ctx = canvasRef.current.getContext("2d")!;
    const visible = Math.floor(text.length * progress);   // ← línea clave

    // limpiar y rotar igual que antes …
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    ctx.save();
    ctx.translate(canvasRef.current.width/2, canvasRef.current.height/2);
    ctx.rotate(-Math.PI/2);
    ctx.translate(-canvasRef.current.height/2, -canvasRef.current.width/2);

    ctx.fillStyle = "#eb6d75";  // fondo
    ctx.fillRect(0,0,canvasRef.current.width, canvasRef.current.height);

    ctx.font = font;
    ctx.fillStyle = color;
    ctx.textBaseline = "top";

    // const words = text.slice(0, visible).split(" ");
    // let line = "", y = padding;
    const maxW = canvasRef.current.width - padding*2;

    const raw      = text.slice(0, visible);   // lo que ya se ha “tecleado”
    const lineH    = parseInt(font, 10) * 1.4; // altura de línea aprox.
    let   y        = padding;                  // posición y inicial
    
    raw.split("\n").forEach(paragraph => {     // ← respeta \n
      let line = "";
      paragraph.split(" ").forEach(word => {
        const test = line ? `${line} ${word}` : word;
        if (ctx.measureText(test).width > maxW) {
          ctx.fillText(line, padding, y);
          line = word;                         // nueva línea
          y   += lineH;
        } else {
          line = test;                         // sigue en la misma línea
        }
      });
      ctx.fillText(line, padding, y);          // último trozo del párrafo
      y += lineH;                              // salto de línea “forzado” (\n)
    });
    // ctx.fillText(line, padding, y);
    ctx.restore();

    textureRef.current.needsUpdate = true;
  }, [text, progress, font, color, padding]);

  return textureRef.current;
};
